import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Droppable } from 'react-beautiful-dnd';
import { Box, Flex, Text, Tooltip, useToast } from '@chakra-ui/react';
import { css } from '@emotion/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isFulfilled } from '@reduxjs/toolkit';

import {
  fetchDimensionItemsGroup,
  getDataIndicatorsKeys,
  getGroupItem,
  getItemId,
  setGroupItem,
  UpdateDimensionProperty,
} from 'redux/ItemPageSlice';
import { AppDispatch } from 'utils/GlobalHelpers';
import { getBaseScenario } from 'redux/ScenarioSlice';
import { getSelectedDriver } from 'redux/PlanPageSlice';
import { usePlanPageRevampVariant } from 'pages/PlanRevamp/PlanPageUIRevamp/usePlanPageUiRevamp';
import Plus from 'components/Icons/Plus';
import { DriverPanelPlusIcon } from 'pages/PlanRevamp/PlanPageUIRevamp/icons/DriverPanelFigmaIcons';
import {
  PLAN_DRIVER_CARD_GAP,
  PLAN_PROPERTY_CARD_SHADOW,
  PLAN_PROPERTY_CARD_WIDTH,
} from 'pages/PlanRevamp/PlanPageUIRevamp/planInputPanelTheme';
import { getPlanPageThemeColors } from 'redux/ThemeSlice';
import { UpdatePropertyValues } from 'models/requestBody/updatePropertyValues.interface';
import CustomToast from 'components/CustomToast';

import apiService from '../../../../services';

import DesktopElements from './DesktopElements';
import AddNewItem from './AddNewItem';
import DesktopItemHeader from './DesktopItemHeader';

interface Properties {
  planPermission: boolean;
  longClickCheck: any;
  isLongClicked: boolean;
  refetchRequest: () => void;
  variant?: 'legacy' | 'revamp';
}

const styles = {
  container: (open: boolean) => css`
    &:hover {
      cursor: pointer;
      transition: all 0.15s ease-out;

      ${!open && 'background: #9588e7'};
    }

    &::before {
      content: '';
      position: absolute;
      right: 50px;
      width: 30px;
      top: 20px;
    }

    .infoButton {
      ${!open && 'display: none'};
    }

    .infoButtonParent:hover .infoButton {
      display: block;
    }
  `,
};

const DesktopItems = ({
  longClickCheck,
  isLongClicked,
  refetchRequest,
  planPermission,
  variant = 'legacy',
}: Properties) => {
  const queryClient = useQueryClient();
  const dispatch: AppDispatch = useDispatch();
  const toast = useToast();
  const isRevamp = usePlanPageRevampVariant(variant);
  const themeColors = useSelector(getPlanPageThemeColors);

  const itemId = useSelector(getItemId);
  const groupItem = useSelector(getGroupItem);
  const baseScenario = useSelector(getBaseScenario);
  const currentDriver = useSelector(getSelectedDriver);
  // const selectedElement = useSelector(getSelectedElement);
  const dataIndicatorsKeys = useSelector(getDataIndicatorsKeys);
  // const [displayGroupInfo, setDisplayGroupInfo]: any = useState(null);

  const { mutate: updateValue } = useMutation(
    ({ item_id, request_body }: UpdatePropertyValues) =>
      apiService.update_property_values_group({ item_id, request_body }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['propertyValues', currentDriver?.block_id]);
        dispatch(fetchDimensionItemsGroup({ currentDriver, scenario_id: baseScenario?.id }));
        refetchRequest();
      },
      onError: (error: any) => {
        toast({
          duration: 2500,
          render: () => (
            <CustomToast title={error?.response?.data?.message || 'Error'} status='error' />
          ),
        });
      },
    },
  );

  const handleItem = (name: string): void => {
    if (groupItem === name) {
      dispatch(setGroupItem(null));
      return;
    }
    dispatch(setGroupItem(name));
  };

  const onSubmitValue = async (value: string | null, id: number, propertyValue: string) => {
    const propertyId = isRevamp
      ? itemId?.properties?.find(
          (element: any) =>
            element.name?.trim().toLowerCase() === propertyValue?.trim().toLowerCase(),
        )?.id
      : itemId?.properties?.find((element: any) => element.name === propertyValue)?.id;

    if (!propertyId) {
      return;
    }

    const requestBody = {
      property_id: propertyId,
      value: value ?? '',
      scenario_id: baseScenario?.id,
    };

    if (isRevamp) {
      const result = await dispatch(
        UpdateDimensionProperty({
          item_id: id,
          request_body: requestBody,
          toaster: {
            successMessage: 'Successfully updated',
            errorMessage: true,
          },
        }),
      );

      if (isFulfilled(result)) {
        queryClient.invalidateQueries(['propertyValues', currentDriver?.block_id]);
        dispatch(fetchDimensionItemsGroup({ currentDriver, scenario_id: baseScenario?.id }));
        refetchRequest();
      }
      return;
    }

    updateValue({
      item_id: id,
      request_body: requestBody,
    });
  };

  return (
    <>
      {dataIndicatorsKeys &&
        dataIndicatorsKeys?.map((dataKey: any, index: number) => {
          const droppableId: string = JSON.stringify({
            id: dataKey?.dim_id,
            name: dataKey?.name,
          });

          return (
            <React.Fragment key={index}>
              <Droppable droppableId={droppableId} key={dataKey?.dim_id}>
                {(provided) => {
                  return (
                    <Flex
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={isRevamp ? undefined : 'dimension'}
                      key={index}
                      mr={isRevamp ? 0 : '15px'}
                      flexDirection='column'
                      gap={isRevamp ? PLAN_DRIVER_CARD_GAP : undefined}
                      minW={isRevamp ? PLAN_PROPERTY_CARD_WIDTH : '310px'}
                      maxW={isRevamp ? PLAN_PROPERTY_CARD_WIDTH : { base: '400px', md: '400px' }}
                      w={isRevamp ? PLAN_PROPERTY_CARD_WIDTH : undefined}
                      alignSelf={isRevamp ? 'stretch' : 'flex-start'}
                      alignItems='flex-start'
                      backgroundColor={isRevamp ? themeColors.surface : 'rgba(67, 64, 111, 0.5)'}
                      border={isRevamp ? '1px solid' : 'none'}
                      borderColor={isRevamp ? themeColors.border : undefined}
                      boxShadow={isRevamp ? PLAN_PROPERTY_CARD_SHADOW : undefined}
                      borderRadius={isRevamp ? '12px' : '18px'}
                      mb={isRevamp ? 0 : '15px'}
                      padding={isRevamp ? '12px' : '5px 10px 0 10px'}
                      h={isRevamp ? '100%' : 'auto'}
                      maxH={isRevamp ? '100%' : undefined}
                    >
                      <DesktopItemHeader
                        variant={variant}
                        isLongClicked={isLongClicked}
                        dataKey={dataKey}
                        planPermission={planPermission}
                        longClickCheck={longClickCheck}
                        refetchRequest={refetchRequest}
                      />
                      <DesktopElements
                        {...{
                          _index: index,
                          dataKey,
                          styles,
                          onSubmitValue,
                          longClickCheck,
                          refetchRequest,
                          isLongClicked,
                          planPermission,
                          variant,
                        }}
                      />

                      <AddNewItem
                        {...{
                          dataKey,
                          styles,
                          planPermission,
                          refetchRequest,
                          variant,
                        }}
                      />

                      <Flex
                        justifyContent='center'
                        alignItems='center'
                        gap={isRevamp ? 2 : undefined}
                        py={isRevamp ? '9px' : undefined}
                        px={isRevamp ? 4 : undefined}
                        pb={isRevamp ? undefined : '15px'}
                        mt={isRevamp ? 0 : undefined}
                        w='100%'
                        bg={isRevamp ? themeColors.surface : undefined}
                        border={isRevamp ? '1px solid' : 'none'}
                        borderColor={isRevamp ? themeColors.border : undefined}
                        borderRadius={isRevamp ? '8px' : undefined}
                        cursor={planPermission ? 'not-allowed' : 'pointer'}
                        onClick={planPermission ? undefined : () => handleItem(dataKey?.name)}
                      >
                        {isRevamp ? (
                          <DriverPanelPlusIcon color={themeColors.primary} />
                        ) : (
                          <Box mr='15px'>
                            <Plus height={5} width={5} />
                          </Box>
                        )}
                        <Tooltip label={currentDriver?.dim_name}>
                          <Text
                            fontSize={isRevamp ? '14px' : '1xl'}
                            fontWeight={isRevamp ? '700' : undefined}
                            fontFamily={isRevamp ? 'bold' : undefined}
                            color={isRevamp ? themeColors.textDark : undefined}
                            pt={isRevamp ? 0 : '2px'}
                            noOfLines={1}
                          >
                            {currentDriver?.dim_name}
                          </Text>
                        </Tooltip>
                      </Flex>
                    </Flex>
                  );
                }}
              </Droppable>
            </React.Fragment>
          );
        })}
    </>
  );
};

export default DesktopItems;
